import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from './ui/Button';
import Card from './ui/Card';
import { useDisplaySettings } from '../store/DisplaySettingsContext';

const textScaleOptions = [
  { value: 1, label: '100%' },
  { value: 1.125, label: '112.5%' },
  { value: 1.25, label: '125%' },
  { value: 1.5, label: '150%' }
];

const buildAnnouncement = (settings) => {
  const size = textScaleOptions.find((option) => option.value === settings.textScale)?.label || '100%';
  const contrast = settings.contrast === 'high' ? 'High contrast on' : 'High contrast off';
  const motion = settings.reducedMotion ? 'Reduced motion on' : 'Reduced motion off';
  const font = settings.dyslexiaFont ? 'Dyslexia friendly font on' : 'Default font';
  const theme = settings.theme === 'dark' ? 'Dark theme' : settings.theme === 'light' ? 'Light theme' : 'Auto theme';
  return `Text size ${size}. ${contrast}. ${motion}. ${font}. ${theme}.`;
};

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

export default function AccessibilitySettingsDialog({ isOpen, onClose }) {
  const { settings, setSettings, reset } = useDisplaySettings();
  const [announcement, setAnnouncement] = useState('');
  const dialogRef = useRef(null);

  const liveMessage = useMemo(() => announcement, [announcement]);

  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = dialog.querySelectorAll(focusableSelector);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first) first.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
      if (event.key !== 'Tab') return;
      if (focusable.length === 0) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    dialog.addEventListener('keydown', handleKeyDown);
    return () => dialog.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setAnnouncement(buildAnnouncement(settings));
  }, [settings, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="sp-dialog-backdrop" role="presentation">
      <div
        className="sp-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="accessibility-title"
        aria-describedby="accessibility-description"
        ref={dialogRef}
      >
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 id="accessibility-title" className="sp-value" style={{ fontSize: '18px', fontWeight: 600 }}>
                Accessibility & Display
              </h2>
              <p id="accessibility-description" className="sp-muted text-sm">
                Adjust text size, contrast, and motion preferences. Changes apply immediately.
              </p>
            </div>
            <Button size="sm" variant="ghost" type="button" onClick={onClose} aria-label="Close accessibility settings">
              Close
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="sp-label">Text size</div>
              <div className="sp-segment" role="radiogroup" aria-label="Text size">
                {textScaleOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={settings.textScale === option.value}
                    className={settings.textScale === option.value ? 'active' : ''}
                    onClick={() => setSettings((prev) => ({ ...prev, textScale: option.value }))}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="sp-label">High contrast</div>
              <label className="sp-inline-toggle">
                <input
                  type="checkbox"
                  checked={settings.contrast === 'high'}
                  onChange={(event) =>
                    setSettings((prev) => ({
                      ...prev,
                      contrast: event.target.checked ? 'high' : 'normal'
                    }))
                  }
                />
                Enable high contrast mode
              </label>
            </div>

            <div className="space-y-2">
              <div className="sp-label">Reduced motion</div>
              <label className="sp-inline-toggle">
                <input
                  type="checkbox"
                  checked={settings.reducedMotion}
                  onChange={(event) =>
                    setSettings((prev) => ({
                      ...prev,
                      reducedMotion: event.target.checked
                    }))
                  }
                />
                Reduce motion & animations
              </label>
            </div>

            <div className="space-y-2">
              <div className="sp-label">Theme</div>
              <div className="sp-segment" role="radiogroup" aria-label="Theme mode">
                {['auto', 'light', 'dark'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={settings.theme === mode}
                    className={settings.theme === mode ? 'active' : ''}
                    onClick={() => setSettings((prev) => ({ ...prev, theme: mode }))}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="sp-label">Dyslexia-friendly font</div>
              <label className="sp-inline-toggle">
                <input
                  type="checkbox"
                  checked={settings.dyslexiaFont}
                  onChange={(event) =>
                    setSettings((prev) => ({
                      ...prev,
                      dyslexiaFont: event.target.checked
                    }))
                  }
                />
                Use dyslexia-friendly font
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs sp-muted" aria-live="polite">
              {liveMessage}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" type="button" onClick={reset}>
                Reset defaults
              </Button>
              <Button size="sm" type="button" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
